import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Alert,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { fetchKycProfilesForCenter } from "../../../services/kycService";
import { countClientActivity } from "../../../services/kycActivityService";
import {
  KYC_STATUS,
  ACCOUNT_STATE,
  CLIENT_CLASSIFICATION,
  APP_USER_STATUS,
  DOCUMENT_UPDATE_STATUS,
  appUserStatusLabel,
  appUserStatusColor,
  documentUpdateStatusLabel,
} from "../../../Utils/kycConstants";
import { shouldHideKycProfile } from "../../../Utils/kycVisibility";
import CustomSearch from "../../../Components/CustomSearch/CustomSearch";
import TableContainer from "../../../Components/TableContainer/TableContainer";
import useDebounce from "../../../Components/useDebounce/useDebounce";
import KycImportPanel from "./components/KycImportPanel";
import KycAddClientDialog from "./components/KycAddClientDialog";
import KycStatusChip from "./components/KycStatusChip";
import KycClientDetailDrawer from "./components/KycClientDetailDrawer";

const FILTER_SELECT_SX = {
  height: "46px",
  borderRadius: "8px",
  backgroundColor: "rgba(255,255,255,0.07)",
  color: "white",
  "& .MuiSelect-icon": { color: "rgba(255,255,255,0.6)" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
};

const MENU_PROPS = {
  PaperProps: {
    sx: {
      bgcolor: "#1e1e2e",
      color: "white",
      "& .MuiMenuItem-root:hover": { bgcolor: "rgba(42,245,152,0.15)" },
    },
  },
};

const TAB_OVERVIEW = 0;
const TAB_EXPIRED = 1;
const TAB_MISSING = 2;
const TAB_WALLETS = 3;
const TAB_UNMATCHED = 4;
const TAB_DOC_UPDATE = 5;

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function sortProfilesAZ(list) {
  return [...list].sort((a, b) => {
    const nameA = (a.display_name || a.folder_name || a.email || "").toLowerCase();
    const nameB = (b.display_name || b.folder_name || b.email || "").toLowerCase();
    return nameA.localeCompare(nameB, "pt-BR", { sensitivity: "base" });
  });
}

const KycControlCenter = () => {
  const [tab, setTab] = useState(TAB_OVERVIEW);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [kycStatusFilter, setKycStatusFilter] = useState("");
  const [appStatusFilter, setAppStatusFilter] = useState(String(APP_USER_STATUS.APPROVED));
  const [classificationFilter, setClassificationFilter] = useState(CLIENT_CLASSIFICATION.INDIVIDUAL);
  const [folderStateFilter, setFolderStateFilter] = useState("");
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [addOpen, setAddOpen] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [activityById, setActivityById] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchKycProfilesForCenter();
      setProfiles(list);
      setSelectedProfile((prev) => {
        if (!prev) return prev;
        const id = prev.user_id || prev.id;
        return list.find((p) => (p.user_id || p.id) === id) || prev;
      });
      const counts = {};
      const needCount = list.filter((p) => {
        const st = Number(p.app_user_status);
        return st === APP_USER_STATUS.REJECTED || st === APP_USER_STATUS.BLOCKED;
      });
      const CHUNK = 6;
      for (let i = 0; i < needCount.length; i += CHUNK) {
        const batch = needCount.slice(i, i + CHUNK);
        await Promise.all(
          batch.map(async (p) => {
            const id = p.user_id || p.id;
            counts[id] = await countClientActivity(id, p.users_doc_id);
          })
        );
      }
      setActivityById(counts);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const existingProfileIds = useMemo(
    () => new Set(profiles.map((p) => p.user_id || p.id)),
    [profiles]
  );

  const hiddenIds = useMemo(() => {
    return new Set(
      profiles
        .filter((p) => shouldHideKycProfile(p, activityById[p.user_id || p.id]))
        .map((p) => p.user_id || p.id)
    );
  }, [profiles, activityById]);

  const visibleProfiles = useMemo(() => {
    if (showHidden) return profiles;
    return profiles.filter((p) => !hiddenIds.has(p.user_id || p.id));
  }, [profiles, hiddenIds, showHidden]);

  const stats = useMemo(() => {
    const approved = visibleProfiles.filter((p) => Number(p.app_user_status) === APP_USER_STATUS.APPROVED).length;
    const expired = visibleProfiles.filter((p) => p.kyc_status === KYC_STATUS.EXPIRED).length;
    const missing = visibleProfiles.filter((p) => p.kyc_status === KYC_STATUS.MISSING_DOCS).length;
    const unmatched = visibleProfiles.filter((p) => p.is_orphan).length;
    const walletIssues = visibleProfiles.filter((p) => {
      const s = p.wallet_recon_summary?.status;
      return s && s !== "match";
    }).length;
    const neverWithdrawn = visibleProfiles.filter(
      (p) => p.wallet_recon_summary?.status === "never_withdrawn"
    ).length;
    const docUpdates = visibleProfiles.filter((p) => {
      const s = p.document_update_request?.status;
      return s === DOCUMENT_UPDATE_STATUS.PENDING || s === DOCUMENT_UPDATE_STATUS.SUBMITTED || s === DOCUMENT_UPDATE_STATUS.REJECTED;
    }).length;
    return {
      approved,
      expired,
      missing,
      unmatched,
      walletIssues,
      neverWithdrawn,
      docUpdates,
      total: visibleProfiles.length,
      hidden: hiddenIds.size,
    };
  }, [visibleProfiles, hiddenIds]);

  const filtered = useMemo(() => {
    let list = [...visibleProfiles];

    if (tab === TAB_EXPIRED) list = list.filter((p) => p.kyc_status === KYC_STATUS.EXPIRED);
    if (tab === TAB_MISSING) list = list.filter((p) => p.kyc_status === KYC_STATUS.MISSING_DOCS);
    if (tab === TAB_WALLETS)
      list = list.filter((p) => {
        const s = p.wallet_recon_summary?.status;
        return s && s !== "match";
      });
    if (tab === TAB_UNMATCHED) list = list.filter((p) => p.is_orphan);
    if (tab === TAB_DOC_UPDATE) {
      list = list.filter((p) => {
        const s = p.document_update_request?.status;
        return s === DOCUMENT_UPDATE_STATUS.PENDING || s === DOCUMENT_UPDATE_STATUS.SUBMITTED || s === DOCUMENT_UPDATE_STATUS.REJECTED;
      });
    }

    if (tab === TAB_OVERVIEW) {
      list = list.filter((p) => !p.is_orphan);
      if (appStatusFilter !== "all") {
        list = list.filter((p) => Number(p.app_user_status) === Number(appStatusFilter));
      }
      if (classificationFilter !== "all") {
        list = list.filter((p) => p.classification === classificationFilter);
      }
    }

    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      list = list.filter(
        (p) =>
          p.display_name?.toLowerCase().includes(q) ||
          p.email?.toLowerCase().includes(q) ||
          p.folder_name?.toLowerCase().includes(q) ||
          p.user_id?.toLowerCase().includes(q)
      );
    }
    if (kycStatusFilter) list = list.filter((p) => p.kyc_status === kycStatusFilter);
    if (folderStateFilter) list = list.filter((p) => p.account_state === folderStateFilter);

    return sortProfilesAZ(list).map((p, i) => ({ ...p, rowid: i + 1 }));
  }, [
    visibleProfiles,
    tab,
    debouncedSearch,
    kycStatusFilter,
    appStatusFilter,
    classificationFilter,
    folderStateFilter,
  ]);

  const paginated = useMemo(() => {
    const start = page * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const openDetail = useCallback((row) => {
    setSelectedProfile(row);
    setDrawerOpen(true);
  }, []);

  const columns = useMemo(() => [
    {
      field: "display_name",
      headerName: "Client",
      width: 280,
      sortable: false,
      cellClassName: "leftAlign",
      headerAlign: "left",
      renderCell: (params) => (
        <Box sx={{ width: "100%", minWidth: 0, textAlign: "left", py: 0.25 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: "rgba(255,255,255,0.96)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.row.display_name || params.row.folder_name || "—"}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "rgba(255,255,255,0.72)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
            }}
          >
            {params.row.email || "—"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "app_user_status",
      headerName: "App status",
      width: 110,
      sortable: false,
      renderCell: (params) => {
        const st = params.row.app_user_status;
        return (
          <Chip
            size="small"
            label={appUserStatusLabel(st)}
            color={appUserStatusColor(st)}
            variant="outlined"
            sx={{ fontWeight: 700, fontSize: 11 }}
          />
        );
      },
    },
    {
      field: "kyc_status",
      headerName: "KYC",
      width: 120,
      sortable: false,
      renderCell: (params) => <KycStatusChip status={params.row.kyc_status} />,
    },
    {
      field: "completeness_score",
      headerName: "Score",
      width: 72,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 700 }}>
          {params.row.completeness_score ?? 0}%
        </Typography>
      ),
    },
    {
      field: "classification",
      headerName: "Type",
      width: 96,
      sortable: false,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.row.classification === CLIENT_CLASSIFICATION.CORPORATE ? "Corp" : "Indiv"}
          variant="outlined"
          sx={{ fontSize: 11, color: "rgba(255,255,255,0.85)" }}
        />
      ),
    },
    {
      field: "contract",
      headerName: "Contract",
      width: 100,
      sortable: false,
      renderCell: (params) => {
        const cs = params.row.contract_summary;
        const ok = cs?.signature_date || cs?.contract_wallet;
        return (
          <Chip
            size="small"
            label={ok ? "On file" : "Gap"}
            color={ok ? "success" : "warning"}
            variant="outlined"
          />
        );
      },
    },
    {
      field: "contract_wallet_address",
      headerName: "Contract wallet",
      width: 190,
      sortable: false,
      cellClassName: "leftAlign",
      headerAlign: "left",
      renderCell: (params) => {
        const address = params.row.contract_summary?.contract_wallet || "—";
        return (
          <Typography
            variant="caption"
            title={address}
            sx={{
              color: "rgba(255,255,255,0.86)",
              fontFamily: "monospace",
              fontSize: 11,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
              width: "100%",
            }}
          >
            {address}
          </Typography>
        );
      },
    },
    {
      field: "wallet_recon",
      headerName: "Wallets",
      width: 128,
      sortable: false,
      renderCell: (params) => {
        const s = params.row.wallet_recon_summary?.status || "—";
        const label = s === "needs_review" ? "Review" : s === "never_withdrawn" ? "No payout" : s;
        const color =
          s === "match" ? "success" : s === "never_withdrawn" ? "info" : s === "—" ? "default" : "warning";
        return (
          <Chip size="small" label={label} color={color} variant="outlined" sx={{ fontSize: 11, fontWeight: 600 }} />
        );
      },
    },
    {
      field: "screening_status",
      headerName: "Screening",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="caption" sx={{ textTransform: "capitalize", color: "rgba(255,255,255,0.85)" }}>
          {params.row.screening_status || "pending"}
        </Typography>
      ),
    },
    {
      field: "identity_id",
      headerName: "ID no.",
      width: 108,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.82)", fontFamily: "monospace", fontSize: 11 }}>
          {params.row.identity_document_number || "—"}
        </Typography>
      ),
    },
    {
      field: "identity_expiry",
      headerName: "ID expiry",
      width: 96,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.82)", fontSize: 11 }}>
          {params.row.identity_expiry_date || "—"}
        </Typography>
      ),
    },
    {
      field: "document_update",
      headerName: "Doc update",
      width: 118,
      sortable: false,
      renderCell: (params) => {
        const s = params.row.document_update_request?.status;
        if (!s) {
          return (
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", fontSize: 11 }}>
              —
            </Typography>
          );
        }
        const color =
          s === DOCUMENT_UPDATE_STATUS.SUBMITTED
            ? "warning"
            : s === DOCUMENT_UPDATE_STATUS.APPROVED
              ? "success"
              : s === DOCUMENT_UPDATE_STATUS.REJECTED
                ? "error"
                : "info";
        return (
          <Chip
            size="small"
            label={documentUpdateStatusLabel(s) || s}
            color={color}
            variant="outlined"
            sx={{ fontSize: 11, fontWeight: 600 }}
          />
        );
      },
    },
    {
      field: "view_detail",
      headerName: "View",
      width: 56,
      sortable: false,
      disableColumnMenu: true,
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip title="Open KYC detail">
          <IconButton
            size="small"
            aria-label="Open KYC detail"
            onClick={(e) => {
              e.stopPropagation();
              openDetail(params.row);
            }}
            sx={{
              color: "#7eb8ff",
              "&:hover": { bgcolor: "rgba(126,184,255,0.15)", color: "#b8d4ff" },
            }}
          >
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [openDetail]);

  const resetOverviewFilters = () => {
    setAppStatusFilter(String(APP_USER_STATUS.APPROVED));
    setClassificationFilter(CLIENT_CLASSIFICATION.INDIVIDUAL);
    setKycStatusFilter("");
    setFolderStateFilter("");
    setSearch("");
    setPage(0);
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <VerifiedUserIcon sx={{ color: "#2af598", fontSize: 32 }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            KYC Control Center
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.65)" }}>
            Approved clients · documents · contracts · wallet reconciliation
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<PersonAddAlt1Icon />}
          onClick={() => setAddOpen(true)}
          sx={{ ml: "auto", borderColor: "rgba(42,245,152,0.5)", color: "#2af598" }}
        >
          Add client to KYC
        </Button>
      </Box>

      <KycAddClientDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        existingProfileIds={existingProfileIds}
        onDone={load}
      />

      <KycImportPanel onDone={load} />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label: "Total profiles", value: stats.total, color: "#2af598" },
          { label: "App approved", value: stats.approved, color: "#81c784" },
          { label: "Expired docs", value: stats.expired, color: "#ef5350" },
          { label: "Missing docs", value: stats.missing, color: "#ff7043" },
          { label: "Wallet gaps", value: stats.walletIssues, color: "#ffb74d" },
          { label: "Unmatched folders", value: stats.unmatched, color: "#b39ddb" },
          { label: "Doc updates", value: stats.docUpdates, color: "#7eb8ff" },
        ].map((c) => (
          <Grid item xs={6} sm={4} md={2} key={c.label}>
            <Card sx={{ bgcolor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Typography variant="caption" sx={{ color: "#888" }}>
                  {c.label}
                </Typography>
                <Typography variant="h6" sx={{ color: c.color, fontWeight: 700 }}>
                  {c.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Tabs
        value={tab}
        onChange={(_, v) => {
          setTab(v);
          setPage(0);
        }}
        sx={{
          mb: 1,
          "& .MuiTab-root": { color: "rgba(255,255,255,0.6)", textTransform: "none", fontWeight: 600 },
          "& .Mui-selected": { color: "#2af598" },
          "& .MuiTabs-indicator": { backgroundColor: "#2af598" },
        }}
      >
        <Tab label="Approved clients (A–Z)" />
        <Tab label={`Expired (${stats.expired})`} />
        <Tab label={`Missing docs (${stats.missing})`} />
        <Tab label={`Wallet issues (${stats.walletIssues})`} />
        <Tab label={`Unmatched (${stats.unmatched})`} />
        <Tab label={`Doc update (${stats.docUpdates})`} />
      </Tabs>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2,
          p: 2,
          borderRadius: 2,
          bgcolor: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Box sx={{ flex: "1 1 220px", minWidth: 180 }}>
          <CustomSearch
            placeholder="Search name, email…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            onClear={() => {
              setSearch("");
              setPage(0);
            }}
          />
        </Box>
        {tab === TAB_OVERVIEW && (
          <>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>App status</InputLabel>
              <Select
                value={appStatusFilter}
                label="App status"
                onChange={(e) => {
                  setAppStatusFilter(e.target.value);
                  setPage(0);
                }}
                sx={FILTER_SELECT_SX}
                MenuProps={MENU_PROPS}
              >
                <MenuItem value={String(APP_USER_STATUS.APPROVED)}>Approved only</MenuItem>
                <MenuItem value={String(APP_USER_STATUS.PENDING)}>Pending</MenuItem>
                <MenuItem value={String(APP_USER_STATUS.REJECTED)}>Rejected</MenuItem>
                <MenuItem value={String(APP_USER_STATUS.BLOCKED)}>Blocked</MenuItem>
                <MenuItem value="all">All statuses</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Client type</InputLabel>
              <Select
                value={classificationFilter}
                label="Client type"
                onChange={(e) => {
                  setClassificationFilter(e.target.value);
                  setPage(0);
                }}
                sx={FILTER_SELECT_SX}
                MenuProps={MENU_PROPS}
              >
                <MenuItem value={CLIENT_CLASSIFICATION.INDIVIDUAL}>Individual</MenuItem>
                <MenuItem value={CLIENT_CLASSIFICATION.CORPORATE}>Corporate</MenuItem>
                <MenuItem value="all">All types</MenuItem>
              </Select>
            </FormControl>
          </>
        )}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>KYC status</InputLabel>
          <Select
            value={kycStatusFilter}
            label="KYC status"
            onChange={(e) => {
              setKycStatusFilter(e.target.value);
              setPage(0);
            }}
            sx={FILTER_SELECT_SX}
            MenuProps={MENU_PROPS}
          >
            <MenuItem value="">All</MenuItem>
            {Object.values(KYC_STATUS).map((s) => (
              <MenuItem key={s} value={s}>
                {s.replace(/_/g, " ")}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Folder</InputLabel>
          <Select
            value={folderStateFilter}
            label="Folder"
            onChange={(e) => {
              setFolderStateFilter(e.target.value);
              setPage(0);
            }}
            sx={FILTER_SELECT_SX}
            MenuProps={MENU_PROPS}
          >
            <MenuItem value="">All</MenuItem>
            {Object.values(ACCOUNT_STATE).map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {tab === TAB_OVERVIEW && (
          <Chip
            label="Reset defaults"
            onClick={resetOverviewFilters}
            sx={{ alignSelf: "center", cursor: "pointer", color: "#7eb8ff" }}
            variant="outlined"
          />
        )}
        <Chip
          label={`Mostrar ocultos (${stats.hidden})`}
          onClick={() => {
            setShowHidden((v) => !v);
            setPage(0);
          }}
          variant={showHidden ? "filled" : "outlined"}
          sx={{
            alignSelf: "center",
            cursor: "pointer",
            color: showHidden ? "#0a1a12" : "#ffb74d",
            bgcolor: showHidden ? "#ffb74d" : "transparent",
            borderColor: "rgba(255,183,77,0.5)",
          }}
        />
      </Box>

      <TabPanel value={tab} index={tab}>
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Default view: approved app users, individual clients, sorted A–Z. Rejected/Blocked accounts with no
          transaction and no ledger are hidden until you turn on &quot;Mostrar ocultos&quot;. Nothing is deleted.
        </Alert>
        <TableContainer
          columns={columns}
          rows={paginated}
          loading={loading}
          page={page}
          pageSize={pageSize}
          totalCount={filtered.length}
          handlePageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
          paginationShow
          hideFillerColumn
          onRowClick={(params) => openDetail(params?.row)}
          rowHeight={64}
          sx={{
            "& .MuiDataGrid-row:nth-of-type(odd)": {
              backgroundColor: "rgba(255,255,255,0.015)",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(42,245,152,0.08) !important",
            },
            "& .MuiDataGrid-cell": {
              color: "rgba(255,255,255,0.9)",
            },
          }}
        />
      </TabPanel>

      <KycClientDetailDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        profile={selectedProfile}
        onUpdated={load}
      />
    </Box>
  );
};

export default KycControlCenter;
